import { createStore, type Store } from '@/state/store';
import { emptySolution, initialState, sequenceView, type AppState, type EngineStatus, type ViewMode } from '@/state/appState';
import { History, deleteString, insertString, isContinuous, lockedAfterDelete, lockedAfterInsert, moveString, snapshot, truncateKeepingLocked } from '@/state/commands';
import { PlaybackController } from '@/state/playback';
import { SequenceBuffer, FloatBuffer } from '@/state/sequenceBuffer';
import { EngineClient } from '@/workers/EngineClient';
import { StringArtRenderer } from '@/renderer/StringArtRenderer';
import { ThreeRenderer } from '@/renderer/ThreeRenderer';
import { buildViewImage } from '@/renderer/views';
import { decodeImage, rasterizeBitmap, imageFilesFromDataTransfer, type RasterizeOptions } from '@/image/decode';
import { renderSample, SAMPLES } from '@/image/samples';
import { exportAs } from '@/export/exporters';
import { downloadBlob } from '@/export/download';
import { parseProject, buildProject, serializeProject } from '@/export/project';
import { cloneConfig, type EngineConfig, type ExportFormat, type Project, type RenderSettings } from '@/types/engine';
import { ToastHost } from '@/components/toast';
import { uid } from '@/components/dom';
import { renderThumbnail } from '@/export/thumbnail';
import type { DesignLibrary, DesignRecord } from '@/state/library';
import { errorMessage } from '@/workers/workerProtocol';
import { buildLayout } from './layout';

export interface AppOptions {
  client?: EngineClient;
  /** Where designs are kept between visits; without one the editor never saves. */
  library?: DesignLibrary | null;
}

/** Saves wait this long after a change, and at most this often while changes keep coming. */
const SAVE_DELAY_MS = 1500;
/** Larger photos are re-encoded before they are kept with the design. */
const KEEP_ORIGINAL_BYTES = 8 * 1024 * 1024;

export class App {
  readonly store: Store<AppState>;
  readonly client: EngineClient;
  readonly history = new History();
  readonly toasts = new ToastHost();
  readonly playback: PlaybackController;
  renderer: StringArtRenderer | null = null;
  three: ThreeRenderer | null = null;

  private readonly sequence = new SequenceBuffer();
  private readonly scores = new FloatBuffer();
  private readonly errors = new FloatBuffer();
  private engineSequenceStale = false;
  private configTimer: number | null = null;
  private pendingConfig: EngineConfig | null = null;
  private configInFlight: Promise<void> = Promise.resolve();
  private lastUnderlayKey = '';
  private lastUnderlay: ImageData | null = null;
  private fpsFrames = 0;
  private fpsTime = 0;
  private statsTimer: number | null = null;
  private readonly library: DesignLibrary | null;
  private saveTimer: number | null = null;
  private savePromise: Promise<void> = Promise.resolve();
  private saveFailed = false;
  /** A photo waiting to be written to the library with the next save. */
  private pendingSource: Blob | null = null;
  private restoring = false;
  private designCreatedAt: number | null = null;
  /** Aborts window listeners on dispose, so a later editor on the same page is not doubled up. */
  private readonly lifetime = new AbortController();
  private alive = true;

  constructor(private readonly root: HTMLElement, options: AppOptions = {}) {
    this.store = createStore(initialState());
    this.client = options.client ?? new EngineClient();
    this.library = options.library ?? null;
    this.playback = new PlaybackController(this.store);
    this.client.on('progress', (m) => this.onProgress(m));
    this.client.on('complete', (m) => this.onComplete(m));
    this.client.on('paused', () => this.setStatus('paused', 'Paused'));
    this.client.on('cancelled', () => this.setStatus('paused', 'Cancelled'));
    this.client.on('error', (e) => this.fail(e.message));
  }

  async boot(): Promise<void> {
    const layout = buildLayout(this);
    this.root.replaceChildren(layout.element, this.toasts.element);
    this.renderer = new StringArtRenderer(layout.stage);
    this.installStageInteraction(layout.stage);
    this.installKeyboard();
    this.installPaste();
    this.installUnload();
    this.store.subscribe((state, previous) => this.onStateChanged(state, previous));
    this.startFpsMeter();
    try {
      const version = await this.client.initialize();
      this.store.set({ wasmVersion: version });
      this.setStatus('idle', 'Drop a photo, paste one, or pick a sample to begin.');
    } catch (err) {
      this.fail(`The WebAssembly engine could not start: ${errorMessage(err)}`);
    }
  }

  // ---------------------------------------------------------------- status

  get state(): AppState {
    return this.store.get();
  }

  setStatus(status: EngineStatus, message = ''): void {
    this.store.set({ status, statusMessage: message, error: status === 'error' ? message : null });
  }

  fail(message: string): void {
    console.error(message);
    this.toasts.show(message, 'error');
    const status = this.state.status;
    this.store.set({ error: message, status: status === 'running' || status === 'preparing' || status === 'loading' ? 'ready' : status, statusMessage: message });
  }

  notify(message: string, kind: 'info' | 'success' = 'info'): void {
    this.toasts.show(message, kind);
  }

  // ---------------------------------------------------------------- sources

  async loadImageFile(file: File | Blob, name?: string): Promise<void> {
    const loaded = await this.loadImage(file, name, { quiet: false });
    if (!loaded || !this.library || this.restoring) return;
    const { source, design } = this.state;
    if (!design.name) this.store.set({ design: { ...design, name: designNameFor(source.name) } });
    try {
      this.pendingSource = await storableSource(file, source.bitmap);
    } catch (err) {
      this.pendingSource = null;
      this.fail(`The photo could not be kept with the design: ${errorMessage(err)}`);
    }
    this.scheduleSave();
  }

  private async loadImage(file: File | Blob, name: string | undefined, { quiet }: { quiet: boolean }): Promise<boolean> {
    if (this.state.status === 'running') await this.pause();
    this.setStatus('loading', 'Decoding image…');
    const t0 = performance.now();
    try {
      const decoded = await decodeImage(file, name ?? (file instanceof File ? file.name : 'image'));
      const previous = this.state.source.bitmap;
      previous?.close();
      this.store.set({
        source: { kind: 'image', name: decoded.name, width: decoded.width, height: decoded.height, bitmap: decoded.bitmap, rgba: null },
      });
      await this.rasterizeAndLoad();
      this.store.set((s) => ({ stats: { ...s.stats, loadMs: performance.now() - t0 } }));
      if (!quiet) this.notify(`Loaded ${decoded.name} (${decoded.width}×${decoded.height}).`, 'success');
      return true;
    } catch (err) {
      this.fail(errorMessage(err));
      return false;
    }
  }

  // ---------------------------------------------------------------- designs

  /** Renames the design; the name is saved with everything else. */
  renameDesign(name: string): void {
    const next = name.trim();
    if (!next || next === this.state.design.name) return;
    this.store.set((s) => ({ design: { ...s.design, name: next } }));
  }

  /** Restores a design from the library: its photo first, then its project on top. */
  async openDesign(id: string): Promise<boolean> {
    if (!this.library) return false;
    let record: DesignRecord | null;
    let source: Blob | null = null;
    try {
      record = await this.library.get(id);
      if (record) source = await this.library.getSource(id);
    } catch (err) {
      this.fail(`Could not read the design: ${errorMessage(err)}`);
      return false;
    }
    if (!record) {
      this.fail('That design is not in this browser.');
      return false;
    }
    const rec = record;
    this.restoring = true;
    try {
      this.designCreatedAt = rec.createdAt;
      this.store.set((s) => ({
        design: { id: rec.id, name: rec.name, save: 'saved' },
        target: { ...s.target, resolution: rec.resolution, cropMode: rec.cropMode },
      }));
      if (source) await this.loadImage(source, rec.sourceName, { quiet: true });
      await this.openProjectText(rec.project);
      return true;
    } finally {
      this.restoring = false;
    }
  }

  /** Writes any pending change now; the router waits for this before leaving the editor. */
  async flushSave(): Promise<void> {
    if (this.saveTimer !== null) {
      window.clearTimeout(this.saveTimer);
      this.saveTimer = null;
      this.savePromise = this.savePromise.then(() => this.saveNow());
    }
    await this.savePromise;
  }

  private watchForChanges(state: AppState, previous: AppState): void {
    if (!this.library || this.restoring || state === previous || state.target.width === 0) return;
    const changed =
      state.solution !== previous.solution ||
      state.config !== previous.config ||
      state.render !== previous.render ||
      state.target !== previous.target ||
      state.exportOptions !== previous.exportOptions ||
      state.design.name !== previous.design.name;
    if (changed) this.scheduleSave();
  }

  /** Saves once changes pause, and at most every SAVE_DELAY_MS while they keep coming. */
  private scheduleSave(): void {
    if (!this.library || this.state.target.width === 0) return;
    if (this.state.design.save !== 'saving') this.store.set((s) => ({ design: { ...s.design, save: 'saving' } }));
    if (this.saveTimer !== null) return;
    this.saveTimer = window.setTimeout(() => {
      this.saveTimer = null;
      this.savePromise = this.savePromise.then(() => this.saveNow());
    }, SAVE_DELAY_MS);
  }

  private async saveNow(): Promise<void> {
    const library = this.library;
    const s = this.state;
    if (!library || s.target.width === 0) return;
    const id = s.design.id ?? uid();
    const now = Date.now();
    this.designCreatedAt ??= now;
    const project = buildProject(s, s.wasmVersion);
    const record: DesignRecord = {
      id,
      name: s.design.name || 'Untitled',
      createdAt: this.designCreatedAt,
      updatedAt: now,
      project: JSON.stringify(project),
      resolution: s.target.resolution,
      cropMode: s.target.cropMode,
      sourceName: s.source.name,
      width: s.target.width,
      height: s.target.height,
      strings: s.solution.count,
      pins: s.solution.pins.length >>> 1,
      thumb: renderThumbnail(project, 320, { blendMode: s.ui.blendMode, roughness: s.ui.stringRoughness, brightness: s.ui.stringBrightness }),
    };
    try {
      const source = this.pendingSource;
      if (source) {
        await library.putSource(id, source);
        if (this.pendingSource === source) this.pendingSource = null;
      }
      await library.put(record);
      this.store.set((st) => ({ design: { ...st.design, id, save: this.saveTimer === null ? 'saved' : 'saving' } }));
      this.saveFailed = false;
    } catch (err) {
      this.store.set((st) => ({ design: { ...st.design, id, save: 'error' } }));
      if (!this.saveFailed) this.fail(`Could not save the design: ${errorMessage(err)}`);
      this.saveFailed = true;
    }
  }

  async loadSample(id: string): Promise<void> {
    const sample = SAMPLES.find((s) => s.id === id);
    if (!sample) return;
    try {
      const blob = await renderSample(sample, 1024);
      await this.loadImageFile(blob, sample.file);
    } catch (err) {
      this.fail(errorMessage(err));
    }
  }

  async loadFilesFromTransfer(transfer: DataTransfer | null): Promise<void> {
    const files = imageFilesFromDataTransfer(transfer);
    if (files.length === 0) {
      const project = Array.from(transfer?.files ?? []).find((f) => /\.json$/i.test(f.name));
      if (project) {
        await this.openProjectFile(project);
        return;
      }
      this.fail('No image file found in the dropped data.');
      return;
    }
    await this.loadImageFile(files[0]);
  }

  /** Re-rasterizes the current bitmap at the configured resolution and loads it. */
  async rasterizeAndLoad(): Promise<void> {
    const { source, target, config } = this.state;
    if (!source.bitmap) return;
    const options: RasterizeOptions = { size: target.resolution, mode: target.cropMode };
    const { rgba, width, height } = rasterizeBitmap(source.bitmap, options);
    const original = rgbaToGray(rgba);
    const copy = rgba.slice();
    this.setStatus('loading', 'Preprocessing target…');
    const processed = await this.client.loadTarget(rgba, width, height, config);
    this.resetSolution();
    this.store.set((s) => ({
      source: { ...s.source, rgba: copy },
      target: { ...s.target, width, height, processed, original },
      needsPrepare: true,
    }));
    this.renderer?.setTargetSize(width, height);
    this.renderer?.resetView();
    this.three?.setTargetSize(width, height);
    await this.refreshPins();
    this.setStatus('ready', `Target ${width}×${height} ready. Press Generate.`);
  }

  async setResolution(resolution: number, cropMode?: 'square' | 'fit'): Promise<void> {
    this.store.set((s) => ({ target: { ...s.target, resolution, cropMode: cropMode ?? s.target.cropMode } }));
    if (this.state.source.bitmap) await this.rasterizeAndLoad();
  }

  // ------------------------------------------------------------- configuration

  /** Updates the engine configuration; changes are pushed to the worker (debounced). */
  updateConfig(mutate: (config: EngineConfig) => void): void {
    const next = cloneConfig(this.state.config);
    mutate(next);
    this.store.set({ config: next });
    this.scheduleConfigPush(next);
  }

  updateRender(mutate: (render: RenderSettings) => void): void {
    const next = cloneConfig(this.state.render);
    mutate(next);
    this.store.set({ render: next });
  }

  updateUi(patch: Partial<AppState['ui']>): void {
    this.store.set((s) => ({ ui: { ...s.ui, ...patch } }));
  }

  private scheduleConfigPush(config: EngineConfig): void {
    this.pendingConfig = config;
    if (this.configTimer !== null) window.clearTimeout(this.configTimer);
    this.configTimer = window.setTimeout(() => {
      this.configTimer = null;
      const cfg = this.pendingConfig;
      this.pendingConfig = null;
      if (cfg) this.configInFlight = this.configInFlight.then(() => this.pushConfig(cfg));
    }, 150);
  }

  private async pushConfig(config: EngineConfig): Promise<void> {
    const { target, status } = this.state;
    if (target.width === 0) return;
    if (status === 'running') return; // applied when the run pauses
    try {
      const change = await this.client.configure(config);
      if (change === 'none' || change === 'live') return;
      this.engineSequenceStale = true;
      this.store.set({ needsPrepare: true });
      if (change === 'reprocess') {
        const snap = await this.client.snapshot();
        this.store.set((s) => ({ target: { ...s.target, processed: snap.target } }));
      }
      if (change === 'reprocess' || change === 'regeneratePins') {
        await this.refreshPins();
      }
    } catch (err) {
      this.fail(errorMessage(err));
    }
  }

  async flushConfig(): Promise<void> {
    if (this.configTimer !== null) {
      window.clearTimeout(this.configTimer);
      this.configTimer = null;
      const cfg = this.pendingConfig;
      this.pendingConfig = null;
      if (cfg) this.configInFlight = this.configInFlight.then(() => this.pushConfig(cfg));
    }
    await this.configInFlight;
  }

  private async refreshPins(): Promise<void> {
    try {
      const pins = await this.client.generatePins();
      this.store.set((s) => ({ solution: { ...s.solution, pins } }));
    } catch (err) {
      this.fail(errorMessage(err));
    }
  }

  // ------------------------------------------------------------- generation

  private resetSolution(): void {
    this.sequence.clear();
    this.scores.clear();
    this.errors.clear();
    this.history.clear();
    this.engineSequenceStale = false;
    this.store.set((s) => ({
      solution: { ...emptySolution(), pins: s.solution.pins },
      playback: { ...s.playback, index: 0, playing: false, follow: true },
      ui: { ...s.ui, selected: null, hovered: null, pendingPin: null },
      history: { canUndo: false, canRedo: false, undoLabel: '', redoLabel: '' },
    }));
  }

  private async ensurePrepared(): Promise<boolean> {
    await this.flushConfig();
    const state = this.state;
    if (state.target.width === 0) {
      this.fail('Load an image before generating.');
      return false;
    }
    if (!state.needsPrepare && !this.engineSequenceStale) return true;
    this.setStatus('preparing', 'Building candidate lines…');
    const t0 = performance.now();
    try {
      const { stats, pins, target } = await this.client.prepare();
      this.store.set((s) => ({
        solution: { ...s.solution, pins, initialError: stats.initialError, error: s.solution.count === 0 ? stats.initialError : s.solution.error },
        target: { ...s.target, processed: target },
        stats: { ...s.stats, prepare: stats, prepareMs: performance.now() - t0 },
        needsPrepare: false,
      }));
      if (this.sequence.count > 0) {
        const r = await this.client.replay(this.sequence.toArray());
        this.store.set((s) => ({ solution: { ...s.solution, error: r.error, current: r.current, currentPin: r.currentPin } }));
      }
      this.engineSequenceStale = false;
      return true;
    } catch (err) {
      this.fail(errorMessage(err));
      return false;
    }
  }

  async generate(): Promise<void> {
    if (this.state.status === 'running') {
      await this.pause();
      return;
    }
    if (!(await this.ensurePrepared())) return;
    try {
      this.store.set((s) => ({ playback: { ...s.playback, playing: false, follow: true }, solution: { ...s.solution, stopReason: null } }));
      this.playback.pause();
      await this.client.start();
      this.setStatus('running', 'Generating…');
      this.startStatsPolling();
    } catch (err) {
      this.fail(errorMessage(err));
    }
  }

  async pause(): Promise<void> {
    if (this.state.status !== 'running') return;
    try {
      await this.client.pause();
      this.setStatus('paused', 'Paused');
      await this.flushConfig();
      if (this.pendingConfig === null && !this.engineSequenceStale) {
        // Push the latest configuration now that the worker is idle.
        await this.pushConfig(this.state.config);
      }
    } catch (err) {
      this.fail(errorMessage(err));
    }
  }

  async cancel(): Promise<void> {
    if (this.state.status !== 'running') return;
    try {
      await this.client.cancel();
      this.setStatus('paused', 'Cancelled');
    } catch (err) {
      this.fail(errorMessage(err));
    }
  }

  async stepOnce(): Promise<void> {
    if (this.state.status === 'running') return;
    if (!(await this.ensurePrepared())) return;
    try {
      const { step, current } = await this.client.step();
      if (step.applied) {
        this.sequence.append(Uint16Array.of(step.from, step.to));
        this.scores.append(Float32Array.of(step.score));
        this.errors.append(Float32Array.of(step.error));
        this.store.set((s) => ({
          solution: { ...s.solution, sequence: this.sequence.array, count: this.sequence.count, scores: this.scores.array, errors: this.errors.array, error: step.error, current, currentPin: step.to, stopReason: null },
          playback: { ...s.playback, index: s.playback.follow ? this.sequence.count : s.playback.index },
        }));
        this.setStatus('paused', `String ${step.index + 1}: pin ${step.from} → ${step.to}`);
      } else {
        this.store.set((s) => ({ solution: { ...s.solution, stopReason: step.stop } }));
        this.setStatus('done', `Stopped: ${describeStop(step.stop)}`);
      }
    } catch (err) {
      this.fail(errorMessage(err));
    }
  }

  async resetSequence(): Promise<void> {
    if (this.state.status === 'running') await this.cancel();
    try {
      if (!this.state.needsPrepare) await this.client.reset();
    } catch (err) {
      this.fail(errorMessage(err));
    }
    this.resetSolution();
    this.setStatus('ready', 'Sequence cleared.');
  }

  private onProgress(m: import('@/workers/workerProtocol').ProgressMessage): void {
    this.sequence.append(m.newLines);
    this.scores.append(m.newScores);
    this.errors.append(m.newErrors);
    this.store.set((s) => ({
      status: 'running',
      solution: {
        ...s.solution,
        sequence: this.sequence.array,
        count: this.sequence.count,
        scores: this.scores.array,
        errors: this.errors.array,
        error: m.error,
        current: m.current ?? s.solution.current,
        currentPin: m.currentPin,
      },
      playback: s.playback.follow ? { ...s.playback, index: this.sequence.count } : s.playback,
      stats: { ...s.stats, stepsPerSecond: m.stepsPerSecond, elapsedMs: m.elapsedMs, wasmMemoryBytes: m.wasmMemoryBytes, batchSize: m.batchSize },
      statusMessage: `Generating… ${this.sequence.count} / ${m.maxStrings} strings, error ${m.error.toFixed(4)}`,
    }));
  }

  private onComplete(m: import('@/workers/workerProtocol').CompleteMessage): void {
    this.store.set((s) => ({
      solution: { ...s.solution, error: m.error, current: m.current, stopReason: m.reason },
      stats: { ...s.stats, elapsedMs: m.elapsedMs },
    }));
    this.setStatus('done', `Finished: ${describeStop(m.reason)} — ${this.sequence.count} strings, error ${m.error.toFixed(4)}.`);
    this.stopStatsPolling();
    void this.refreshStats();
  }

  private startStatsPolling(): void {
    this.stopStatsPolling();
    this.statsTimer = window.setInterval(() => void this.refreshStats(), 1000);
  }

  private stopStatsPolling(): void {
    if (this.statsTimer !== null) {
      window.clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
  }

  async refreshStats(): Promise<void> {
    if (this.state.target.width === 0) return;
    try {
      const engine = await this.client.stats();
      this.store.set((s) => ({ stats: { ...s.stats, engine } }));
    } catch {
      // stats are best effort
    }
  }

  // ------------------------------------------------------------- editing

  private async commitSequence(label: string, next: Uint16Array, locked: ReadonlySet<number>, options: { record?: boolean } = {}): Promise<void> {
    const before = snapshot(this.sequence.view(), this.state.solution.locked);
    this.sequence.replace(next);
    this.scores.truncate(Math.min(this.scores.count, this.sequence.count));
    this.errors.truncate(Math.min(this.errors.count, this.sequence.count));
    if (options.record !== false) {
      this.history.push({ label, before, after: snapshot(next, locked) });
    }
    this.store.set((s) => ({
      solution: { ...s.solution, sequence: this.sequence.array, count: this.sequence.count, scores: this.scores.array, errors: this.errors.array, locked: new Set(locked), stopReason: null },
      playback: { ...s.playback, index: Math.min(s.playback.index, this.sequence.count), follow: s.playback.index >= this.sequence.count },
      ui: { ...s.ui, selected: s.ui.selected !== null && s.ui.selected < this.sequence.count ? s.ui.selected : null },
      history: this.historyState(),
    }));
    await this.syncEngineSequence();
  }

  private historyState(): AppState['history'] {
    return { canUndo: this.history.canUndo, canRedo: this.history.canRedo, undoLabel: this.history.undoLabel, redoLabel: this.history.redoLabel };
  }

  private async syncEngineSequence(): Promise<void> {
    if (this.state.needsPrepare || this.state.target.width === 0) {
      this.engineSequenceStale = true;
      return;
    }
    if (this.state.status === 'running') await this.pause();
    try {
      // The client transfers buffers, so hand over a copy of the shared sequence.
      const r = await this.client.replay(this.sequence.toArray());
      this.engineSequenceStale = false;
      this.store.set((s) => ({ solution: { ...s.solution, error: r.error, current: r.current, currentPin: r.currentPin } }));
      if (this.errors.count < this.sequence.count) {
        // Errors for edited prefixes are unknown until re-run; keep what we have.
      }
    } catch (err) {
      this.engineSequenceStale = true;
      this.fail(errorMessage(err));
    }
  }

  async deleteSelected(): Promise<void> {
    const { ui, solution } = this.state;
    if (ui.selected === null) return;
    const index = ui.selected;
    const next = deleteString(sequenceView(solution), index);
    await this.commitSequence(`Delete string ${index}`, next, lockedAfterDelete(solution.locked, index));
  }

  async moveSelected(delta: number): Promise<void> {
    const { ui, solution } = this.state;
    if (ui.selected === null) return;
    const from = ui.selected;
    const to = Math.max(0, Math.min(solution.count - 1, from + delta));
    if (to === from) return;
    const next = moveString(sequenceView(solution), from, to);
    const locked = new Set<number>();
    for (const i of solution.locked) {
      if (i === from) locked.add(to);
      else if (from < to && i > from && i <= to) locked.add(i - 1);
      else if (from > to && i >= to && i < from) locked.add(i + 1);
      else locked.add(i);
    }
    await this.commitSequence(`Move string ${from} → ${to}`, next, locked);
    this.updateUi({ selected: to });
  }

  async addManualString(from: number, to: number, at?: number): Promise<void> {
    const { solution } = this.state;
    const pinCount = solution.pins.length >>> 1;
    if (from === to || from < 0 || to < 0 || from >= pinCount || to >= pinCount) {
      this.fail(`Pins must be two different ids between 0 and ${pinCount - 1}.`);
      return;
    }
    const position = at ?? solution.count;
    const next = insertString(sequenceView(solution), position, from, to);
    const locked = lockedAfterInsert(solution.locked, position);
    locked.add(position);
    await this.commitSequence(`Add string ${from} → ${to}`, next, locked);
    this.updateUi({ selected: position, pendingPin: null });
    this.playback.seek(this.sequence.count);
  }

  toggleLock(index: number): void {
    const { solution } = this.state;
    if (index < 0 || index >= solution.count) return;
    const locked = new Set(solution.locked);
    if (locked.has(index)) locked.delete(index);
    else locked.add(index);
    this.store.set((s) => ({ solution: { ...s.solution, locked } }));
  }

  /** Cuts the sequence at `count` (keeping locked strings) and continues optimizing. */
  async regenerateFrom(count: number): Promise<void> {
    const { solution } = this.state;
    const { sequence, locked } = truncateKeepingLocked(sequenceView(solution), solution.locked, count);
    await this.commitSequence(`Regenerate from string ${count}`, sequence, locked);
    this.playback.seek(this.sequence.count);
    await this.generate();
  }

  async truncateToPlayhead(): Promise<void> {
    const { playback, solution } = this.state;
    const { sequence, locked } = truncateKeepingLocked(sequenceView(solution), solution.locked, playback.index);
    await this.commitSequence(`Truncate to ${playback.index} strings`, sequence, locked);
  }

  async undo(): Promise<void> {
    const entry = this.history.undo();
    if (!entry) return;
    await this.commitSequence(entry.label, entry.before.sequence, entry.before.locked, { record: false });
    this.store.set({ history: this.historyState() });
    this.notify(`Undo: ${entry.label}`);
  }

  async redo(): Promise<void> {
    const entry = this.history.redo();
    if (!entry) return;
    await this.commitSequence(entry.label, entry.after.sequence, entry.after.locked, { record: false });
    this.store.set({ history: this.historyState() });
    this.notify(`Redo: ${entry.label}`);
  }

  selectString(index: number | null): void {
    this.updateUi({ selected: index });
  }

  pinClicked(index: number): void {
    const { ui } = this.state;
    if (ui.tool !== 'addString') return;
    if (ui.pendingPin === null) {
      this.updateUi({ pendingPin: index });
      return;
    }
    if (ui.pendingPin === index) {
      this.updateUi({ pendingPin: null });
      return;
    }
    void this.addManualString(ui.pendingPin, index);
  }

  // ------------------------------------------------------------- export / project

  async export(format: ExportFormat, pngSize = 2048, pngSoftware = false): Promise<void> {
    try {
      if (format !== 'json' && format !== 'png' && this.engineSequenceStale) {
        if (!(await this.ensurePrepared())) return;
      }
      const result = await exportAs(format, { state: this.state, client: this.client, renderer: this.renderer, wasmVersion: this.state.wasmVersion, pngSize, pngSoftware });
      downloadBlob(result.blob, result.filename);
      this.notify(`Exported ${result.filename}`, 'success');
    } catch (err) {
      this.fail(errorMessage(err));
    }
  }

  buildProject(): Project {
    return buildProject(this.state, this.state.wasmVersion);
  }

  async saveProject(): Promise<void> {
    await this.export('json');
  }

  async openProjectFile(file: File): Promise<void> {
    await this.openProjectText(await file.text());
  }

  async openProjectText(text: string): Promise<void> {
    let project: Project;
    try {
      project = parseProject(text);
    } catch (err) {
      this.fail(errorMessage(err));
      return;
    }
    if (this.state.status === 'running') await this.pause();
    const pins = new Float32Array(project.pinCoordinates.length * 2);
    project.pinCoordinates.forEach(([x, y], i) => {
      pins[i * 2] = x;
      pins[i * 2 + 1] = y;
    });
    const seq = new Uint16Array(project.sequence.length * 2);
    project.sequence.forEach(([a, b], i) => {
      seq[i * 2] = a;
      seq[i * 2 + 1] = b;
    });
    this.sequence.replace(seq);
    this.scores.clear();
    this.errors.clear();
    this.history.clear();
    const { target } = this.state;
    const dimsMatch = target.width === project.target.width && target.height === project.target.height;
    this.store.set((s) => ({
      config: project.config,
      render: project.render,
      solution: { ...emptySolution(), pins, sequence: this.sequence.array, count: this.sequence.count },
      playback: { ...s.playback, index: this.sequence.count, playing: false, follow: true },
      target: dimsMatch ? s.target : { ...s.target, width: project.target.width, height: project.target.height, resolution: Math.max(project.target.width, project.target.height), processed: null, original: null },
      history: { canUndo: false, canRedo: false, undoLabel: '', redoLabel: '' },
      needsPrepare: true,
    }));
    this.renderer?.setTargetSize(project.target.width, project.target.height);
    this.renderer?.resetView();
    this.three?.setTargetSize(project.target.width, project.target.height);
    if (dimsMatch) {
      try {
        const r = await this.client.applyProject(serializeProject(project));
        this.scores.replace(r.scores);
        this.errors.replace(r.errors);
        this.engineSequenceStale = false;
        this.store.set((s) => ({
          solution: { ...s.solution, pins: r.pins, scores: this.scores.array, errors: this.errors.array, error: r.error, current: r.current, currentPin: r.sequence.length > 0 ? r.sequence[r.sequence.length - 1] : 0 },
          target: { ...s.target, processed: r.target },
          needsPrepare: false,
        }));
        this.setStatus('ready', `Project loaded: ${project.sequence.length} strings. You can continue generating.`);
      } catch (err) {
        this.fail(errorMessage(err));
      }
    } else {
      this.engineSequenceStale = true;
      this.setStatus('ready', `Project loaded (${project.sequence.length} strings). Load the source image "${project.source.name}" at ${project.target.width}×${project.target.height} to continue optimizing.`);
    }
  }

  // ------------------------------------------------------------- rendering

  private onStateChanged(state: AppState, previous: AppState): void {
    this.watchForChanges(state, previous);
    if (!this.renderer) return;
    const r = this.renderer;
    const { solution, render, ui, playback, target } = state;
    if (solution.pins !== previous.solution.pins || solution.sequence !== previous.solution.sequence || solution.count !== previous.solution.count) {
      r.setSolution(solution.pins, solution.sequence.subarray(0, solution.count * 2));
      this.three?.setSolution(solution.pins, solution.sequence.subarray(0, solution.count * 2));
    }
    if (render !== previous.render || ui.blendMode !== previous.ui.blendMode || ui.stringRoughness !== previous.ui.stringRoughness || ui.stringBrightness !== previous.ui.stringBrightness) {
      r.setStyle({
        color: render.stringColor,
        opacity: render.stringOpacity,
        thickness: render.stringThickness,
        background: render.backgroundColor,
        blendMode: ui.blendMode,
        roughness: ui.stringRoughness,
        brightness: ui.stringBrightness,
      });
      this.three?.setStyle({ color: render.stringColor, opacity: render.stringOpacity, background: render.backgroundColor, pinColor: render.pinColor, showPins: render.showPins || ui.showPins });
    }
    if (ui.renderer !== previous.ui.renderer) void this.switchRenderer(ui.renderer);
    const underlay = this.underlayFor(ui.view, state);
    r.render({
      upTo: playback.index,
      underlay,
      underlayOpacity: ui.view === 'strings' ? 0 : ui.underlayOpacity,
      overlay: {
        showPins: ui.showPins,
        showPinIds: ui.showPinIds,
        showCandidates: ui.showCandidates,
        pinColor: render.pinColor,
        pinRadius: render.pinRadius,
        selected: ui.selected,
        hovered: ui.hovered,
        currentPin: state.status === 'running' || state.status === 'paused' || ui.showCandidates ? solution.currentPin : null,
        pendingPin: ui.pendingPin,
        locked: solution.locked,
      },
    });
    if (this.three && (playback.index !== previous.playback.index || solution.count !== previous.solution.count)) this.three.setUpTo(playback.index);
    if (target.width !== previous.target.width || target.height !== previous.target.height) {
      r.setTargetSize(target.width || 1, target.height || 1);
    }
  }

  private underlayFor(view: ViewMode, state: AppState): ImageData | null {
    if (view === 'strings') return null;
    const { target, solution } = state;
    const key = `${view}:${target.width}x${target.height}:${identity(target.original)}:${identity(target.processed)}:${identity(solution.current)}`;
    if (key === this.lastUnderlayKey) return this.lastUnderlay;
    this.lastUnderlayKey = key;
    this.lastUnderlay = buildViewImage(view, { original: target.original, processed: target.processed, current: solution.current, width: target.width, height: target.height });
    return this.lastUnderlay;
  }

  private async switchRenderer(kind: '2d' | '3d'): Promise<void> {
    const stage = this.renderer?.container;
    if (!stage) return;
    if (kind === '3d' && !this.three) {
      try {
        this.three = await ThreeRenderer.create(stage);
        const { solution, target, render, ui } = this.state;
        this.three.setTargetSize(target.width || 1, target.height || 1);
        this.three.setSolution(solution.pins, sequenceView(solution));
        this.three.setStyle({ color: render.stringColor, opacity: render.stringOpacity, background: render.backgroundColor, pinColor: render.pinColor, showPins: render.showPins || ui.showPins });
        this.three.setUpTo(this.state.playback.index);
      } catch (err) {
        this.fail(`3D renderer unavailable: ${errorMessage(err)}`);
        this.updateUi({ renderer: '2d' });
        return;
      }
    }
    if (kind === '2d' && this.three) {
      this.three.dispose();
      this.three = null;
    }
    stage.classList.toggle('is-3d', kind === '3d');
  }

  private installStageInteraction(stage: HTMLElement): void {
    let dragging: { x: number; y: number; moved: boolean } | null = null;
    let hoverTimer: number | null = null;
    stage.addEventListener('wheel', (e) => {
      if (this.state.ui.renderer === '3d') return;
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      const factor = Math.exp(-e.deltaY * 0.0015);
      this.renderer?.zoomAt(factor, e.clientX - rect.left, e.clientY - rect.top);
    }, { passive: false });
    stage.addEventListener('pointerdown', (e) => {
      if (this.state.ui.renderer === '3d') return;
      const pan = e.button === 1 || this.state.ui.tool === 'pan' || e.shiftKey;
      dragging = { x: e.clientX, y: e.clientY, moved: false };
      if (pan) stage.setPointerCapture(e.pointerId);
    });
    stage.addEventListener('pointermove', (e) => {
      if (this.state.ui.renderer === '3d') return;
      if (dragging && (e.buttons & 1 || e.buttons & 4)) {
        const dx = e.clientX - dragging.x;
        const dy = e.clientY - dragging.y;
        if (Math.abs(dx) + Math.abs(dy) > 2) dragging.moved = true;
        if (dragging.moved && (this.state.ui.tool === 'pan' || e.shiftKey || e.buttons & 4)) {
          this.renderer?.panBy(dx, dy);
          dragging.x = e.clientX;
          dragging.y = e.clientY;
        }
        return;
      }
      if (hoverTimer !== null) return;
      hoverTimer = window.setTimeout(() => {
        hoverTimer = null;
        const rect = stage.getBoundingClientRect();
        const hit = this.renderer?.hitTest(e.clientX - rect.left, e.clientY - rect.top, this.state.playback.index) ?? null;
        const hovered = hit?.kind === 'string' ? hit.index : null;
        if (hovered !== this.state.ui.hovered) this.updateUi({ hovered });
        stage.style.cursor = hit ? 'pointer' : this.state.ui.tool === 'pan' ? 'grab' : 'default';
      }, 40);
    });
    stage.addEventListener('pointerup', (e) => {
      if (this.state.ui.renderer === '3d') return;
      const wasDrag = dragging?.moved ?? false;
      dragging = null;
      if (wasDrag || e.button !== 0) return;
      const rect = stage.getBoundingClientRect();
      const hit = this.renderer?.hitTest(e.clientX - rect.left, e.clientY - rect.top, this.state.playback.index) ?? null;
      if (hit?.kind === 'pin') {
        if (this.state.ui.tool === 'addString') this.pinClicked(hit.index);
        else this.notify(`Pin ${hit.index}`);
        return;
      }
      this.selectString(hit?.kind === 'string' ? hit.index : null);
    });
    stage.addEventListener('pointerleave', () => {
      if (this.state.ui.hovered !== null) this.updateUi({ hovered: null });
    });
    stage.addEventListener('dblclick', () => this.renderer?.resetView());
    stage.addEventListener('dragover', (e) => e.preventDefault());
    stage.addEventListener('drop', (e) => {
      e.preventDefault();
      void this.loadFilesFromTransfer(e.dataTransfer);
    });
  }

  private installKeyboard(): void {
    window.addEventListener('keydown', (e) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        void this.undo();
      } else if ((mod && e.key.toLowerCase() === 'y') || (mod && e.shiftKey && e.key.toLowerCase() === 'z')) {
        e.preventDefault();
        void this.redo();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (this.state.ui.selected !== null) {
          e.preventDefault();
          void this.deleteSelected();
        }
      } else if (e.key === ' ') {
        e.preventDefault();
        this.playback.toggle();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.playback.step(e.shiftKey ? 10 : 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.playback.step(e.shiftKey ? -10 : -1);
      } else if (e.key === 'Home') {
        this.playback.restart();
      } else if (e.key === 'End') {
        this.playback.toEnd();
      } else if (e.key === 'Escape') {
        this.updateUi({ selected: null, pendingPin: null, tool: 'select' });
      } else if (e.key.toLowerCase() === 'g' && !mod) {
        void this.generate();
      }
    }, { signal: this.lifetime.signal });
  }

  private installPaste(): void {
    window.addEventListener('paste', (e) => {
      const files = imageFilesFromDataTransfer(e.clipboardData);
      if (files.length > 0) {
        e.preventDefault();
        void this.loadImageFile(files[0], 'pasted-image.png');
      }
    }, { signal: this.lifetime.signal });
  }

  private installUnload(): void {
    window.addEventListener('pagehide', () => void this.flushSave(), { signal: this.lifetime.signal });
  }

  private startFpsMeter(): void {
    const tick = (t: number) => {
      if (!this.alive) return;
      this.fpsFrames++;
      if (t - this.fpsTime >= 1000) {
        const fps = (this.fpsFrames * 1000) / (t - this.fpsTime);
        this.fpsFrames = 0;
        this.fpsTime = t;
        if (this.state.ui.debugOpen) this.store.set((s) => ({ stats: { ...s.stats, fps: Math.round(fps), renderMs: this.renderer?.renderMs ?? 0 } }));
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  dispose(): void {
    void this.flushSave();
    this.alive = false;
    this.lifetime.abort();
    this.playback.dispose();
    this.stopStatsPolling();
    this.renderer?.dispose();
    this.three?.dispose();
    void this.client.dispose();
  }
}

/** "portrait.jpg" becomes "portrait"; an empty name becomes "Untitled". */
export function designNameFor(fileName: string): string {
  const base = fileName.replace(/\.[a-z0-9]+$/i, '').trim();
  return base || 'Untitled';
}

/** The photo as it will be kept: the file itself, or a JPEG at 2048 px when the file is large. */
async function storableSource(file: Blob, bitmap: ImageBitmap | null): Promise<Blob> {
  if (file.size <= KEEP_ORIGINAL_BYTES || !bitmap) return file;
  const scale = Math.min(1, 2048 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('the photo could not be re-encoded'))), 'image/jpeg', 0.9);
  });
}

function identity(v: object | null): number {
  if (!v) return 0;
  let id = ids.get(v);
  if (id === undefined) {
    id = ++nextId;
    ids.set(v, id);
  }
  return id;
}
const ids = new WeakMap<object, number>();
let nextId = 0;

function rgbaToGray(rgba: Uint8Array): Uint8Array {
  const out = new Uint8Array(rgba.length >>> 2);
  for (let i = 0, j = 0; j < rgba.length; i++, j += 4) {
    const a = rgba[j + 3] / 255;
    const luma = 0.299 * rgba[j] + 0.587 * rgba[j + 1] + 0.114 * rgba[j + 2];
    out[i] = Math.round(luma * a + 255 * (1 - a));
  }
  return out;
}

export function describeStop(reason: string | null): string {
  switch (reason) {
    case 'maxStrings':
      return 'maximum number of strings reached';
    case 'noImprovement':
      return 'no candidate improves the result further';
    case 'deadEnd':
      return 'the current pin has no admissible candidates';
    case 'cancelled':
      return 'cancelled';
    default:
      return reason ?? 'unknown';
  }
}
